import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { type SortingState } from "@tanstack/react-table";
import type {
  ColDef,
  ICellRendererParams,
  CellClickedEvent
} from "ag-grid-community";
import { Checkbox, CheckboxSize } from "shared/components";
import {
  AgGrid,
  DEFAULT_AG_GRID_VIEWPORT_OFFSET
} from "shared/components/AgGrid";
import { Lead } from "views/leads-id/types";
import { useLayout } from "providers/Layout/hooks";
import { formatDate } from "views/applications-id-report/utils";
import { LastNoteCell } from "shared/app/Notes/components/LastNoteCell";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { LEAD_ACTIVITY_TYPE_LABELS } from "shared/app/LeadsTableView/slices/LeadsTable/constants";
import { SelectPopUp } from "shared/app/LeadsTableView/slices/LeadsTable/components/SelectPopUp";
import { Stage } from "views/settings-crm/types/stage";
import { StageSelect } from "shared/app/LeadsTableView/slices/LeadsTable/components/StageSelect";
import { AssigneeSelect } from "shared/app/LeadsTableView/slices/LeadsTable/components/AssigneeSelect";
import { StarCell } from "shared/app/LeadsTableView/slices/LeadsTable/components/StarCell";
import { HeaderWithSort } from "shared/app/TaskTableView/slices/TaskTable/HeaderWithSort";
import { ColumnVisibilityItem } from "providers/Auth";
import {
  LEADS_COLUMN_ORDER,
  LeadColumnId
} from "shared/app/LeadsTableView/constants";

const CHECKBOX_COL_ID = "checkbox";
const STAR_COL_ID = "star";

export const LeadsTable = ({
  data,
  stagesData,
  isLoading,
  pagination,
  perPageRows,
  viewPortHeightOffset,
  sorting,
  onSortingChange,
  columnsVisibility
}: {
  data?: Lead[];
  stagesData: Stage[];
  isLoading?: boolean;
  pagination?: React.ReactNode;
  perPageRows?: number;
  viewPortHeightOffset?: number;
  sorting: SortingState;
  onSortingChange: (next: SortingState) => void;
  columnsVisibility: ColumnVisibilityItem[];
}) => {
  const navigate = useNavigate();
  const dictionary = useUnitsPropertiesDictionary();
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());

  const clearSelectedRows = () => {
    setSelectedRows(new Set());
  };

  const toggleRowSelection = (id: number) => {
    setSelectedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) newSet.delete(id);
      else newSet.add(id);
      return newSet;
    });
  };

  const handleSort = (columnKey: string) => {
    const existingSort = sorting.find((s) => s.id === columnKey);

    let newSorting: SortingState;

    if (existingSort)
      newSorting = sorting.map((s) =>
        s.id === columnKey ? { ...s, desc: !s.desc } : s
      );
    else newSorting = [{ id: columnKey, desc: true }];

    onSortingChange(newSorting);
  };

  const getSortDirection = (columnKey: string) => {
    const sortItem = sorting.find((s) => s.id === columnKey);
    if (!sortItem) return undefined;
    return sortItem.desc ? "DESC" : ("ASC" as const);
  };

  const stageOptions = useMemo(() => {
    return stagesData.map((option) => ({
      label: String(option.name),
      value: Number(option.id),
      color: option.color
    }));
  }, [stagesData]);

  const columns = useMemo<ColDef<Lead>[]>(() => {
    const checkboxCol: ColDef<Lead> = {
      colId: CHECKBOX_COL_ID,
      headerName: "",
      width: 52,
      minWidth: 52,
      pinned: "left",
      resizable: false,
      sortable: false,
      suppressMovable: true,
      cellRenderer: (params: ICellRendererParams<Lead>) => {
        const row = params.data;
        if (!row) return null;
        return (
          <div
            className="flex items-center justify-center w-full h-full"
            onClick={(e) => e.stopPropagation()}
          >
            <Checkbox
              wrapperClassName="!w-auto !flex-none !p-3 justify-center"
              size={CheckboxSize.SM}
              checked={selectedRows.has(row.id)}
              onChange={() => toggleRowSelection(row.id)}
            />
          </div>
        );
      }
    };

    const starCol: ColDef<Lead> = {
      colId: STAR_COL_ID,
      headerName: "",
      width: 52,
      minWidth: 52,
      pinned: "left",
      resizable: false,
      sortable: false,
      suppressMovable: true,
      cellRenderer: (params: ICellRendererParams<Lead>) => {
        const row = params.data;
        if (!row) return null;
        return <StarCell leadId={row.id} starred={row.starred} />;
      }
    };

    const colDefById: Record<LeadColumnId, ColDef<Lead>> = {
      [LeadColumnId.Name]: {
        colId: LeadColumnId.Name,
        headerName: "Name",
        minWidth: 150,
        resizable: true,
        cellRenderer: (params: ICellRendererParams<Lead>) => {
          const row = params.data;
          if (!row) return null;
          return (
            <div className="flex flex-col gap-1">
              <p className="text-content-1000 font-medium text-sm truncate">
                {row.first_name} {row.last_name}
              </p>
              <p className="text-content-400 text-xs font-normal truncate">
                {row.primary_email}
              </p>
            </div>
          );
        }
      },
      [LeadColumnId.PropertyInterested]: {
        colId: LeadColumnId.PropertyInterested,
        headerName: `${dictionary.Property} interested`,
        minWidth: 150,
        resizable: true,
        cellRenderer: (params: ICellRendererParams<Lead>) => {
          const row = params.data;
          if (!row) return null;
          return (
            <div className="flex flex-col gap-1">
              <p className="text-content-1000 font-normal text-sm truncate">
                {row.property?.name || "-"}
              </p>
              <p className="text-content-400 text-xs font-normal truncate">
                {row.property?.address.one_line_address || "-"}
              </p>
            </div>
          );
        }
      },
      [LeadColumnId.UnitInterested]: {
        colId: LeadColumnId.UnitInterested,
        headerName: `${dictionary.Unit} interested`,
        minWidth: 150,
        resizable: true,
        cellRenderer: (params: ICellRendererParams<Lead>) => {
          const row = params.data;
          if (!row) return null;
          return (
            <p className="text-content-1000 font-normal text-sm truncate">
              {row.unit?.title || "-"}
            </p>
          );
        }
      },
      [LeadColumnId.Source]: {
        colId: LeadColumnId.Source,
        headerName: "Source",
        minWidth: 200,
        resizable: true,
        cellRenderer: (params: ICellRendererParams<Lead>) => {
          const row = params.data;
          if (!row) return null;
          return (
            <p className="text-content-1000 font-medium text-sm truncate">
              {row.source?.name || "-"}
            </p>
          );
        }
      },
      [LeadColumnId.LastNote]: {
        colId: LeadColumnId.LastNote,
        headerName: "Last note",
        minWidth: 200,
        width: 300,
        resizable: true,
        sortable: false,
        headerComponent: () => (
          <HeaderWithSort
            label="Last note"
            columnKey={LeadColumnId.LastNote}
            sortDirection={getSortDirection(LeadColumnId.LastNote)}
            onSort={handleSort}
          />
        ),
        cellRenderer: (params: ICellRendererParams<Lead>) => {
          const row = params.data;
          if (!row) return null;
          const note = row.last_note
            ? {
                id: "",
                author: {
                  id: 0,
                  name: row.last_note.actor_name,
                  email: "",
                  author_role: "" as never
                },
                author_role: "" as never,
                message: row.last_note.rich_text?.document_text ?? "",
                rich_text: row.last_note.rich_text ?? undefined,
                created_at: row.last_note.occurred_at
              }
            : null;
          return (
            <div onClick={(e) => e.stopPropagation()}>
              <LastNoteCell note={note} />
            </div>
          );
        }
      },
      [LeadColumnId.RecentActivity]: {
        colId: LeadColumnId.RecentActivity,
        headerName: "Most recent activity",
        width: 270,
        minWidth: 220,
        resizable: true,
        sortable: false,
        headerComponent: () => (
          <HeaderWithSort
            label="Most recent activity"
            columnKey={LeadColumnId.RecentActivity}
            sortDirection={getSortDirection(LeadColumnId.RecentActivity)}
            onSort={handleSort}
          />
        ),
        cellRenderer: (params: ICellRendererParams<Lead>) => {
          const row = params.data;
          if (!row) return null;
          const recentActivity = row.recent_activity_summary;
          return (
            <div className="space-y-2">
              <p className="text-content-1000 font-medium text-sm truncate">
                {recentActivity
                  ? LEAD_ACTIVITY_TYPE_LABELS[recentActivity.activity_type]
                  : "-"}
              </p>
              <p className="text-content-300 text-xs font-normal">
                {recentActivity
                  ? formatDate(recentActivity.occurred_at, true)
                  : "-"}
              </p>
            </div>
          );
        }
      },
      [LeadColumnId.AssignedTo]: {
        colId: LeadColumnId.AssignedTo,
        headerName: "Assigned to",
        minWidth: 240,
        resizable: true,
        cellRenderer: (params: ICellRendererParams<Lead>) => {
          const row = params.data;
          if (!row) return null;
          return (
            <AssigneeSelect
              value={row.assignees?.[0]?.id ?? row.partner_user_id ?? null}
              rowId={String(row.id)}
            />
          );
        }
      },
      [LeadColumnId.StageSelector]: {
        colId: LeadColumnId.StageSelector,
        headerName: "Current stage",
        minWidth: 240,
        resizable: true,
        cellRenderer: (params: ICellRendererParams<Lead>) => {
          const row = params.data;
          if (!row) return null;
          return (
            <StageSelect
              inputClassName="w-[210px]"
              value={row.current_stage?.id}
              options={stageOptions}
              rowId={String(row.id)}
            />
          );
        }
      }
    };

    const visibilityById = new Map(
      columnsVisibility.map((c) => [c.id, c] as const)
    );

    const orderedDynamicCols = LEADS_COLUMN_ORDER.reduce<ColDef<Lead>[]>(
      (acc, colId) => {
        const visibility = visibilityById.get(colId);
        const shouldRender =
          !visibility || visibility.disabled || visibility.checked;
        if (shouldRender) acc.push(colDefById[colId]);
        return acc;
      },
      []
    );

    return [checkboxCol, starCol, ...orderedDynamicCols];
  }, [selectedRows, stageOptions, dictionary, sorting, columnsVisibility]);

  const rows = useMemo<Lead[]>(() => data || [], [data]);

  const { sectionHeight } = useLayout();

  const selectedRowState = useMemo(() => {
    const state: Record<string, boolean> = {};
    selectedRows.forEach((id) => {
      state[String(id)] = true;
    });
    return state;
  }, [selectedRows]);

  const onCellClicked = useCallback(
    (event: CellClickedEvent<Lead>) => {
      const row = event.data;
      if (!row) return;
      const colId = event.column.getColId();
      if (
        colId === CHECKBOX_COL_ID ||
        colId === STAR_COL_ID ||
        colId === "assigned_to" ||
        colId === "stage_selector"
      )
        return;
      navigate(`/leads/${row.id}`);
    },
    [navigate]
  );

  return (
    <div className="relative">
      <AgGrid<Lead>
        viewPortHeight={sectionHeight}
        viewPortHeightOffset={
          viewPortHeightOffset ?? DEFAULT_AG_GRID_VIEWPORT_OFFSET
        }
        columns={columns}
        rows={rows}
        loading={isLoading}
        onCellClicked={onCellClicked}
        footer={pagination}
        showFooter={Boolean(pagination)}
        perPageRows={perPageRows}
        rowHeight={72}
        headerHeight={44}
      />
      <SelectPopUp
        selectedRow={selectedRowState}
        clearSelectedRows={clearSelectedRows}
        stageOptions={stageOptions}
      />
    </div>
  );
};
